import { useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { Mail as MailIcon } from "lucide-react"
import { toast } from "@/components/portal/Toast"
import { cn } from "@/lib/utils"
import { wegSchritt } from "@/lib/wege"

/**
 * Die Ordnerleiste des Posteingangs — Standard-Ordner und IMAP-Ordner, beide
 * als Drop-Ziel fuer Mails.
 *
 * Aus pages/EmailsList.tsx herausgeloest (Schritt G).
 */

/** Die Kennung, unter der eine gezogene Auswahl ihren Herkunftsordner
 *  mitschickt. Ohne die waere „Rueckgaengig" nur ein Wort. */
export const HERKUNFT_MIME = "application/x-email-origin"

function idsAusDrop(ev: React.DragEvent): number[] {
  const raw = ev.dataTransfer.getData("application/x-email-ids") || ev.dataTransfer.getData("text/plain")
  if (!raw) return []
  try {
    const v = JSON.parse(raw)
    return Array.isArray(v) ? v.filter((n) => Number.isFinite(n)) : []
  } catch {
    const n = Number(raw)
    return Number.isFinite(n) && n ? [n] : []
  }
}

async function bulkVerschieben(ids: number[], ziel: string): Promise<void> {
  // #44: dieser Weg hat #28 und #37 hervorgebracht — er gehoert gezaehlt.
  wegSchritt(ids.length > 1 ? "mails_gesammelt_verschoben" : "mail_verschoben")
  const r = await fetch("/api/v1/emails/bulk", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ ids, action: "move", folder: ziel }),
  })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
}

/**
 * Mails per Ziehen verschieben — mit Rueckgaengig (Schritt H).
 *
 * Ein Fehlgriff beim Ziehen war bisher nur durch Suchen wieder einzufangen:
 * die Mails waren weg, und wohin, stand nur kurz im Toast. Jetzt liegt der
 * Rueckweg zehn Sekunden lang als Knopf daneben.
 *
 * Die Herkunft kommt aus dem Drag-Payload. Fehlt sie (aeltere Ansicht, fremde
 * Quelle), wird ohne Rueckgaengig verschoben statt zu raten — eine Mail in
 * einen falsch geratenen Ordner zurueckzuschieben waere schlimmer als kein
 * Knopf.
 *
 * 🔴 `nachher` muss AUCH die Auswahl leeren. Sie liegt in `EmailsList`
 * (`selected`), diese Datei kommt nicht daran — also gehoert sie in den
 * Rueckruf. Ohne das zeigt die Leiste weiter „n ausgewaehlt" und meint Mails,
 * die hier gar nicht mehr liegen; ein Klick auf „Loeschen" traf dann die
 * gerade verschobenen (#37, gemeldet 25.08.2026: „Wenn man mehrere mails
 * markiert und dann in einen adnere ordner verschiebt sind sie immer noch
 * markiert"). Der Bulk-Weg macht es laengst richtig.
 */
export async function verschiebenMitUndo(
  ev: React.DragEvent, ziel: string, nachher: () => void,
): Promise<void> {
  const ids = idsAusDrop(ev)
  const herkunft = (ev.dataTransfer.getData(HERKUNFT_MIME) || "").trim()
  if (!ids.length) return
  try {
    await bulkVerschieben(ids, ziel)
    nachher()
    const wieviele = `${ids.length} E-Mail${ids.length === 1 ? "" : "s"}`
    if (!herkunft || herkunft === ziel) {
      toast(`✓ ${wieviele} → ${ziel}`)
      return
    }
    toast(`✓ ${wieviele} → ${ziel}`, "success", {
      label: "Rueckgaengig",
      onClick: () => {
        bulkVerschieben(ids, herkunft)
          .then(() => { toast(`Zurueck nach ${herkunft}`); nachher() })
          .catch((e) => toast("Rueckgaengig fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error"))
      },
    })
  } catch (e) {
    toast("Verschieben fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error")
  }
}

/** Standard-Ordner-Eintrag (INBOX / Sent / Archive / Trash). Drop-Target fuer Mails. */
export function StandardFolderItem({
  folderDef, active, count, tenant, onClick, onVerschoben,
}: {
  /** `keinAblageziel`: Sammelansicht (z. B. „Spam" über alle Postfächer) —
   *  kein echter IMAP-Ordner, also kein Ziel für Ziehen-und-Ablegen. */
  folderDef: { id: string; name: string; icon: React.ReactNode; keinAblageziel?: boolean }
  active: boolean
  count: number
  tenant: string
  onClick: () => void
  /** Nach dem Verschieben — hier raeumt die Liste ihre Auswahl weg (#37). */
  onVerschoben?: () => void
}) {
  const [dragOver, setDragOver] = useState(false)
  const qc = useQueryClient()
  const onDrop = async (ev: React.DragEvent) => {
    ev.preventDefault()
    setDragOver(false)
    if (!tenant || folderDef.keinAblageziel) return
    await verschiebenMitUndo(ev, folderDef.id, () => {
      qc.invalidateQueries({ queryKey: ["emails"] })
      onVerschoben?.()
    })
  }
  return (
    <div
      className={cn("ev2-folder-item", active && "active")}
      onClick={onClick}
      onDragOver={folderDef.keinAblageziel ? undefined : (ev) => { ev.preventDefault(); setDragOver(true) }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      style={dragOver ? { background: "rgba(99,102,241,0.18)", outline: "2px dashed #6366f1" } : undefined}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
        {folderDef.icon}
        {folderDef.name}
      </span>
      {count > 0 && <span className="ev2-folder-badge">{count}</span>}
    </div>
  )
}

/** IMAP-Ordner-Sidebar-Eintrag mit Kontextmenue (Rechtsklick) + Drop-Target fuer Mails. */
export function FolderItem({
  name, active, count, tenant, postfach, onClick, onAction, onVerschoben,
}: {
  name: string
  active: boolean
  count: number
  tenant: string
  /** Gewähltes Postfach ("" = alle → Aktionen gehen ans Hauptpostfach). */
  postfach?: string
  onClick: () => void
  onAction: () => void
  /** Nach dem Verschieben — hier raeumt die Liste ihre Auswahl weg (#37). */
  onVerschoben?: () => void
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const qc = useQueryClient()

  const doRename = async () => {
    setMenuOpen(false)
    const next = prompt(`Ordner "${name}" umbenennen zu:`, name)
    if (!next || next.trim() === name) return
    try {
      const r = await fetch("/api/v1/folders", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ tenant, old_name: name, new_name: next.trim(), postfach: postfach || null }),
      })
      const d = await r.json().catch(() => ({}))
      if (r.ok) { toast("✓ Umbenannt"); onAction() }
      else toast(d.detail || d.error || "Fehler", "error")
    } catch (e) {
      toast("Fehler: " + (e instanceof Error ? e.message : String(e)), "error")
    }
  }

  const doDelete = async () => {
    setMenuOpen(false)
    if (!confirm(`Ordner "${name}" wirklich löschen? (nur wenn leer)`)) return
    try {
      const r = await fetch("/api/v1/folders", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ tenant, name, postfach: postfach || null }),
      })
      const d = await r.json().catch(() => ({}))
      if (r.ok) { toast("✓ Gelöscht"); onAction() }
      else toast(d.detail || d.error || "Fehler", "error")
    } catch (e) {
      toast("Fehler: " + (e instanceof Error ? e.message : String(e)), "error")
    }
  }

  const onDrop = async (ev: React.DragEvent) => {
    ev.preventDefault()
    setDragOver(false)
    await verschiebenMitUndo(ev, name, () => {
      qc.invalidateQueries({ queryKey: ["emails"] })
      onVerschoben?.()
    })
  }

  return (
    <div
      className={cn("ev2-folder-item", active && "active")}
      onClick={onClick}
      onDragOver={(ev) => { ev.preventDefault(); setDragOver(true) }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      style={{
        position: "relative",
        ...(dragOver ? { background: "rgba(99,102,241,0.18)", outline: "2px dashed #6366f1" } : {}),
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        <MailIcon className="w-[12px] h-[12px] opacity-50" />
        {name}
      </span>
      {count > 0 && <span className="ev2-folder-badge">{count}</span>}
      <button
        type="button"
        onClick={(ev) => { ev.stopPropagation(); setMenuOpen((v) => !v) }}
        title="Optionen"
        style={{
          background: "none", border: "none", cursor: "pointer",
          color: "var(--text-muted)", fontSize: 16, padding: "0 4px",
          marginLeft: 4, lineHeight: 1,
        }}
      >
        ⋮
      </button>
      {menuOpen && (
        <>
          {/* Overlay zum Schliessen beim Klick daneben */}
          <div
            onClick={(ev) => { ev.stopPropagation(); setMenuOpen(false) }}
            style={{ position: "fixed", inset: 0, zIndex: 99 }}
          />
          <div
            style={{
              position: "absolute", right: 4, top: "100%", zIndex: 100,
              background: "var(--bg-card)", border: "1px solid var(--border-color)",
              borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              padding: 4, minWidth: 160,
            }}
            onClick={(ev) => ev.stopPropagation()}
          >
            <button
              type="button"
              onClick={doRename}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "6px 10px", background: "none", border: "none", cursor: "pointer", fontSize: 12 }}
            >
              ✏ Umbenennen
            </button>
            <button
              type="button"
              onClick={doDelete}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "6px 10px", background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "#dc2626" }}
            >
              🗑 Löschen
            </button>
          </div>
        </>
      )}
    </div>
  )
}

/** Activity-Log — zeigt was alles an dieser Mail passiert ist + Vorgang.
 *   1:1 Port von admin/emails.py renderTicketSection. */
